
import React from "react";
import {
  HelpCircle,
  BookOpen,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserAccess } from "@/hooks/useUserAccess";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface HelpMenuProps {
  onNavigate: (path: string) => void;
  isExpanded: boolean;
}

export function HelpMenu({ onNavigate, isExpanded }: HelpMenuProps) {
  const [isAdmin, setIsAdmin] = React.useState(false);
  const { user } = useAuth();
  const { checkUserAccess } = useUserAccess();

  React.useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) return;

      try {
        // Use the checkUserAccess function to determine if the user is an admin
        const userAccess = await checkUserAccess();
        setIsAdmin(userAccess.isAdmin);
      } catch (error) {
        console.error("Error checking admin status:", error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user, checkUserAccess]);

  // All menu items as a single array
  const menuItems = [
    {
      icon: <ShieldCheck size={18} className="text-blue-500" />,
      label: "Admin Dashboard",
      onClick: () => onNavigate("/admin"),
      disabled: !isAdmin
    },
    {
      icon: <HelpCircle size={18} className="text-blue-500" />,
      label: "Help",
      onClick: () => onNavigate("/help"),
      disabled: false
    },
    {
      icon: <BookOpen size={18} className="text-blue-500" />,
      label: "FAQs",
      onClick: () => onNavigate("/faq"),
      disabled: false
    },
    {
      icon: <Mail size={18} className="text-blue-500" />,
      label: "Contact",
      onClick: () => onNavigate("/contact"),
      disabled: false
    }
  ];

  if (isExpanded) {
    return (
      <div className="px-4 py-2">
        <div className="flex flex-col space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.label}
              onClick={item.onClick}
              disabled={item.disabled}
              className={`flex w-full items-center rounded-md transition-colors px-2 py-2 text-sm ${
                item.disabled
                  ? "text-gray-500 cursor-not-allowed opacity-50"
                  : "hover:bg-gray-800 text-gray-300 hover:text-white"
              }`}
            >
              {item.icon}
              <span className="ml-3">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-2">
      <div className="flex flex-col space-y-2">
        {menuItems.map((item) => (
          <TooltipProvider key={item.label}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={item.onClick}
                  disabled={item.disabled}
                  className={`flex items-center justify-center rounded-md transition-colors ${
                    item.disabled
                      ? "text-gray-500 cursor-not-allowed opacity-50"
                      : "hover:bg-gray-800 text-gray-300 hover:text-white"
                  } px-2 py-2 text-sm w-full`}
                >
                  {item.icon}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{item.label}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
    </div>
  );
}
